import React, { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";

import { useDispatch, useSelector } from "react-redux";

import { getFunctions } from "helpers/leads/leadConnection/getFunctions";
import { postConnection } from "helpers/leads/stepHelpers/step3Helper";

import LeadConnectionFilter from "components/LeadConnectionFilter";
import WizardStepLayout from "components/organisms/WizardStepLayout";

import { getSchedules } from "../../redux/slices/leadSources/leadSources.asyncActions";
import SwalAlertComp from "../SwalAlert";

const LeadsStep3 = (props) => {
    const [connectionName, setConnectionName] = useState("");
    const [backfillEnabled, setBackfillEnabled] = useState(false);
    const [backfillMaxDays, setBackfillMaxDays] = useState(7);
    const [backfillDaysError, setBackfillDaysError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
    const dispatch = useDispatch();
    const AllFields = useSelector(
        (state) => state.stepOneDatas.sourceDestinationFields,
    );
    const language = useSelector((state) => state.languagesState.languages);
    const bussiness = useSelector(
        (state) => state.allDashboardDatasState.getAllDashboardDatas.bussiness,
    );
    const template = useSelector(
        (state) => state.leadslastAddedSourceDestinationState.template,
    );
    const sourceTypeCode = useSelector(
        (state) => state.stepOneDatas.data?.source_type_code,
    );

    const BACKFILL_SUPPORTED_SOURCES = ["fl", "lnkdn", "tiktok"];
    const isBackfillSupported =
        BACKFILL_SUPPORTED_SOURCES.includes(sourceTypeCode);

    const [editFilterOptions, setEditFilterOptions] = useState([]);
    const [connectionFilters, setConnectionFilters] = useState([]);
    const [filterValidation, setFilterValidation] = useState(true);

    const toPrevious = () => {
        props.previousStep();
    };

    useEffect(() => {
        dispatch(getSchedules());
    }, [dispatch]);

    useEffect(() => {
        setIsLoading(false);
    }, []);

    const handleBackfillDaysChange = (e) => {
        const raw = e.target.value;

        if (raw === "") {
            setBackfillMaxDays("");
            setBackfillDaysError("Please enter a value between 1 and 7.");
            return;
        }

        const parsed = Number(raw);

        if (!Number.isInteger(parsed) || parsed < 1 || parsed > 7) {
            setBackfillMaxDays(raw);
            setBackfillDaysError("Please enter a value between 1 and 7.");
            return;
        }

        setBackfillMaxDays(parsed);
        setBackfillDaysError("");
    };

    const isConnectionNameEmpty = connectionName.trim() === "";
    const isBackfillInvalid =
        isBackfillSupported &&
        backfillEnabled &&
        (backfillDaysError || backfillMaxDays === "");

    const toNext = async () => {
        setIsSubmitAttempted(true);

        if (isConnectionNameEmpty || isBackfillInvalid || !filterValidation) {
            return;
        }

        setIsLoading(true);

        let businessId =
            bussiness &&
            bussiness.length > 0 &&
            bussiness.find((element) => element.selected === true);
        let data = {
            business_id: businessId?.id ?? "",
            schedule_id: "",
            template_id: template?.template_id ?? "",
            name: connectionName,
            mapping_id: AllFields?.mapping_id,
            filters:
                Object.keys(connectionFilters).length > 0 &&
                connectionFilters?.comparings.length > 0
                    ? connectionFilters
                    : null,
            ...(isBackfillSupported && {
                backfill_enabled: backfillEnabled ? 1 : 0,
                backfill_max_days: backfillEnabled
                    ? Number(backfillMaxDays)
                    : 0,
            }),
        };
        let response = await postConnection(data);
        if (window.dataLayer) {
            window.dataLayer.push({
                event: "connectionCreated",
                business_id: businessId?.id ?? "",
                connectionName: connectionName,
            });
        }
        if (response.status) {
            setIsLoading(false);
            props.nextStep();
        } else {
            setIsLoading(false);
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message && response.message,
                icon: "error",
            });
        }
    };

    useEffect(() => {
        if (connectionName && props?.setConnectionName !== undefined) {
            props?.setConnectionName(connectionName);
        }
    }, [connectionName]);

    const handleFiltersSave = async (filters) => {
        setConnectionFilters(filters);
    };

    useEffect(() => {
        getSourceAndDestinationData();
    }, []);

    const getSourceAndDestinationData = async () => {
        let response = await getFunctions();
        if (response.status) {
            setEditFilterOptions(response.data?.filters);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message && response.message,
                icon: "error",
            });
        }
    };

    return (
        <>
            <WizardStepLayout
                primaryTestId="lead-publish"
                backTestId="lead-customize-back"
                maxWidth="100%"
                onBack={toPrevious}
                backLabel={language?.buttons?.back ?? "Back"}
                onPrimary={toNext}
                primaryAction="complete"
                primaryLabel={language?.buttons?.publish ?? "Publish"}
                isLoading={isLoading}
            >
                <Box
                    className="mb-4"
                    style={{
                        maxWidth: "1280px",
                    }}
                >
                    <div className="wizard-title-area mb-3">
                        <p>
                            <strong>
                                {language?.lead?.connection_name ??
                                    "Connection Name"}
                            </strong>
                            {language?.lead_wizard?.connection_name_desc ??
                                "Enter a meaningful name to identify this connection in your dashboard."}
                        </p>
                    </div>
                    <FormControl
                        style={{
                            width: "100%",
                            maxWidth: "400px",
                        }}
                    >
                        <TextField
                            data-cy="lead-connection-name"
                            autoFocus
                            label={
                                language?.lead?.connection_name ??
                                "Connection Name"
                            }
                            variant="outlined"
                            onChange={(e) => setConnectionName(e.target.value)}
                            placeholder={
                                language?.lead?.add_connection_name ??
                                "Add connection name"
                            }
                            value={connectionName}
                            className="tour-connection-name-input"
                            error={isSubmitAttempted && isConnectionNameEmpty}
                            helperText={
                                isSubmitAttempted && isConnectionNameEmpty
                                    ? "Connection name is required"
                                    : ""
                            }
                        />
                    </FormControl>
                </Box>

                <Divider sx={{ maxWidth: "1280px", mb: 4 }} />

                {isBackfillSupported && (
                    <>
                        <Box
                            className="mb-4"
                            style={{
                                maxWidth: "1280px",
                            }}
                        >
                            <div className="wizard-title-area mb-3">
                                <p>
                                    <strong>
                                        {language?.lead?.backfill_leads ??
                                            "Backfill Leads"}
                                    </strong>
                                    {language?.lead?.backfill_leads_desc ??
                                        "This option allows you to retrieve leads generated before your connection was set up, going back up to 7 days. This is a one-time backfill process."}
                                </p>
                            </div>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: 2,
                                    maxWidth: "400px",
                                }}
                            >
                                <FormControlLabel
                                    sx={{ m: 0 }}
                                    control={
                                        <Checkbox
                                            data-cy="lead-backfill-enabled"
                                            checked={backfillEnabled}
                                            onChange={(e) =>
                                                setBackfillEnabled(
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                    }
                                    label={
                                        language?.lead?.enable_backfill ??
                                        "Enable backfill"
                                    }
                                />
                                {backfillEnabled && (
                                    <FormControl fullWidth>
                                        <TextField
                                            data-cy="lead-backfill-days"
                                            label={
                                                language?.lead
                                                    ?.backfill_max_days ??
                                                "Backfill Days"
                                            }
                                            variant="outlined"
                                            type="number"
                                            value={backfillMaxDays}
                                            onChange={handleBackfillDaysChange}
                                            error={!!backfillDaysError}
                                            helperText={
                                                backfillDaysError ||
                                                "Enter a number of days (1-7)."
                                            }
                                            inputProps={{
                                                min: 1,
                                                max: 7,
                                                step: 1,
                                            }}
                                            onKeyDown={(e) => {
                                                if (
                                                    e.key === "." ||
                                                    e.key === "," ||
                                                    e.key === "-" ||
                                                    e.key === "+" ||
                                                    e.key === "e"
                                                ) {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                    </FormControl>
                                )}
                            </Box>
                        </Box>

                        <Divider sx={{ maxWidth: "1280px", mb: 4 }} />
                    </>
                )}

                {editFilterOptions && (
                    <Box
                        style={{
                            maxWidth: "1280px",
                        }}
                    >
                        <LeadConnectionFilter
                            operators={editFilterOptions?.operators}
                            fields={
                                props?.filterFieldList !== undefined &&
                                props?.filterFieldList
                            }
                            functions={editFilterOptions?.functions}
                            handleSave={handleFiltersSave}
                            isWizard={true}
                            description={
                                language?.lead_wizard?.filters_desc ??
                                "Optionally add conditions to process only the leads that match your criteria. Filters are optional."
                            }
                            setValidationStatus={setFilterValidation}
                            triggerValidation={isSubmitAttempted}
                        />
                    </Box>
                )}
            </WizardStepLayout>
        </>
    );
};

export default LeadsStep3;
